import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { RawMaterialService } from '../../raw-material.service';
import { finalize, Subscription } from 'rxjs';
import {
  RawMaterialCategoryItem,
  RawMaterialEntry,
  RawMaterialItem,
  RawMaterialWarehouse,
} from '../../raw-material.model';
import { PaginatedResponse, TableColumn } from '../../../common/models/common.model';
import { User } from '../../../authentication/auth.models';
import { SettingsService } from '../../../settings/settings.service';
import { UserRole } from '../../../employees/employees.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { Company } from '../../../production-inventory/production-inventory.model';
import { SupplierService } from '../../../purchased-order/services/supplier.service';
import { buildFilterQuery } from '../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-raw-material-entry',
  standalone: false,
  templateUrl: './raw-material-entry.component.html',
  styleUrl: './raw-material-entry.component.scss',
})
export class RawMaterialEntryComponent implements OnInit, OnDestroy {
  @Input() wareHouses: RawMaterialWarehouse[] = [];
  @ViewChild('exportModal') exportModalRef!: TemplateRef<any>;

  protected isRawMaterialEntriesDataLoading: boolean = false;
  protected selectedWarehouse: string | null = null;
  protected selectedWarehouseName: string = '';
  protected rawMaterialEntries: RawMaterialEntry[] = [];
  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;
  protected totalCount: number = 0;
  protected currentUser: User | null = null;
  protected currentPage: number = 1;

  protected searchTerm: string = '';
  protected selectedCompany: string | null = null;
  protected selectedCategory: string | null = null;
  protected selectedSupplier: string | null = null;

  protected companies: Company[] = [];
  protected categories: RawMaterialCategoryItem[] = [];
  protected suppliers: { id: string; name: string }[] = [];
  protected rawMaterials: RawMaterialItem[] = [];

  protected exportFromDate: string = '';
  protected exportToDate: string = '';
  protected exportCompany: string | null = null;
  protected exportWarehouse: string | null = null;
  protected exportCategory: string | null = null;
  protected exportRawMaterial: string | null = null;
  protected isExporting: boolean = false;
  protected exportError: any = null;

  private rawMaterialSubscription!: Subscription;
  private _subs = new Subscription();
  private searchDebounceTimer: any;

  constructor(
    private rawMaterialService: RawMaterialService,
    private settingsService: SettingsService,
    private companyFactoryService: CompanyFactoryService,
    private supplierService: SupplierService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.settingsService.currentUser$.subscribe(user => {
      this.currentUser = user;

      if (this.currentUser?.role !== UserRole.ADMIN) {
        const currentPlanHeadWareHouse = this.wareHouses.find(wareHouse => {
          return wareHouse.plant_head?.user_id === this.currentUser?.id;
        });

        if (currentPlanHeadWareHouse) {
          this.selectedWarehouse = currentPlanHeadWareHouse.id;
          this.selectedWarehouseName = currentPlanHeadWareHouse.name;
        }
      }
    });

    this.getRawMaterialEntryContent();
    this._loadFilterData();
  }

  protected onWareHouseSelected(warehouse: string): void {
    this.rawMaterialEntries = [];

    this.selectedWarehouse = warehouse;
    const changedWarehouse: RawMaterialWarehouse = this.wareHouses.find(
      warehouse => warehouse.id === this.selectedWarehouse
    )!;
    this.selectedWarehouseName = changedWarehouse?.name || '';

    this.getRawMaterialEntryContent();
  }

  protected onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.getRawMaterialEntryContent();
    }, 500);
  }

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.getRawMaterialEntryContent();
  }

  private getRawMaterialEntryContent(): void {
    this.isRawMaterialEntriesDataLoading = true;

    const q = buildFilterQuery({
      page: this.currentPage,
      warehouse: this.selectedWarehouse,
      search: this.searchTerm,
      company: this.selectedCompany,
      category: this.selectedCategory,
      supplier: this.selectedSupplier,
    });

    this.rawMaterialSubscription = this.rawMaterialService
      .get_entries(q)
      .pipe(finalize(() => (this.isRawMaterialEntriesDataLoading = false)))
      .subscribe({
        next: (rawMaterialEntryResponse: PaginatedResponse<RawMaterialEntry>) => {
          this.rawMaterialEntries = rawMaterialEntryResponse.results;
          this.totalCount = rawMaterialEntryResponse.count;
          this.hasNextPage = !!rawMaterialEntryResponse.next;
          this.hasPreviousPage = !!rawMaterialEntryResponse.previous;
        },
      });
  }

  private _loadFilterData(): void {
    this._subs.add(
      this.companyFactoryService.listCompanies().subscribe({
        next: companies => (this.companies = companies),
      })
    );
    this._subs.add(
      this.rawMaterialService.get_categories().subscribe({
        next: categories => (this.categories = categories),
      })
    );
    this._subs.add(
      this.supplierService.light_list_suppliers().subscribe({
        next: suppliers => (this.suppliers = suppliers),
      })
    );
    this._subs.add(
      this.rawMaterialService.list().subscribe({
        next: materials => (this.rawMaterials = materials),
      })
    );
  }

  protected openExportModal(): void {
    this.exportFromDate = '';
    this.exportToDate = '';
    this.exportCompany = null;
    this.exportWarehouse = null;
    this.exportCategory = null;
    this.exportRawMaterial = null;
    this.exportError = null;
    this.modalService.open(this.exportModalRef, { size: 'md' });
  }

  protected onExportSubmit(modalRef: any): void {
    this.isExporting = true;
    this.exportError = null;

    const q: Record<string, any> = {};
    if (this.exportFromDate) q['from_date'] = this.exportFromDate;
    if (this.exportToDate) q['to_date'] = this.exportToDate;
    if (this.exportCompany) q['company'] = this.exportCompany;
    if (this.exportWarehouse) q['warehouse'] = this.exportWarehouse;
    if (this.exportCategory) q['category'] = this.exportCategory;
    if (this.exportRawMaterial) q['raw_material'] = this.exportRawMaterial;

    this.rawMaterialService
      .export_entries(q)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'raw_material_entries.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          modalRef.close();
        },
        error: err => {
          this.exportError = err;
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getRawMaterialEntryContent();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  protected readonly entryColumns: TableColumn<RawMaterialEntry>[] = [
    { label: 'Company', field: 'company_name' },
    { label: 'Category', field: 'raw_material_category' },
    { label: 'Name', field: 'raw_material_name' },
    { label: 'Code', field: 'raw_material_code' },
    { label: 'Quantity', field: 'quantity_received' },
    {
      label: 'Unit',
      field: 'unit_short_name',
      format: (val: string) => (val ? val.toUpperCase() : ''),
    },
    { label: 'PO Number', field: 'po_number' },
    { label: 'Supplier Name', field: 'supplier_info.name' },
    {
      label: 'Invoice Date',
      field: 'invoice_date',
      format: (val: string) => (val ? new Date(val).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''),
    },
    {
      label: 'Received Date',
      field: 'date_received',
      format: (val: string) => (val ? new Date(val).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''),
    },
    { label: 'Warehouse', field: 'warehouse', format: (val: any) => val?.name || '' },
  ];

  protected readonly UserRole = UserRole;
}
